import {Injectable, Signal, signal} from '@angular/core';
import {environment} from "../../../environments/environment";
import {Project} from "../models/project.model";
import {ActivatedRoute} from "@angular/router";
import {HttpClient} from "@angular/common/http";
import {HTTP_HEADERS} from "./core.service";
import {map, tap, catchError} from "rxjs/operators";
import {Observable, throwError} from "rxjs";
import {ToastrService} from "ngx-toastr";

const projectDetailURL = environment.apiUrl + 'generation/--projectID--';
const themeSettingsURL = environment.themeSettingsUrl;
const getContractsJSONUrl = environment.contractsApiUrl;
const projectSettingsURL = environment.projectSettingsURL;

const activeProjectsURL = environment.apiUrl + 'ActiveProjects';

interface ProjectResult {
  result?: Project;
  success: boolean;
  message?: string;
  errorCode?: number;
}

@Injectable({
  providedIn: 'root'
})
export class ProjectsService {
  public logo: string | null = null;
  public loading: boolean = true;
  public gdprLink: string | null = null;
  public activeProjects: Project[] = [];
  public online = false;
  public error: any;
  public errorNotOnline = false;
  public globalActiveProjects = signal<Project[] | undefined>(undefined);
  public project: Project = new Project();
  public projectClaim: string | null = null;
  public projectLine1: string | null = null;
  public projectLine2: string | null = null;


  public globalLoadingSignal = signal<boolean | Signal<boolean>>(false);

  constructor(
    private activatedRoute: ActivatedRoute,
    private http: HttpClient,
    public toastrService: ToastrService
  ) {
  }

  public getProject(projectID: string): Observable<Project> {
    const partnerTokenId = this.activatedRoute.snapshot.queryParamMap.get('partnerTokenId');
    let url = projectDetailURL;
    if (partnerTokenId) {
      url = projectDetailURL + '?partnerToken=' + partnerTokenId;
    } else {
      url = projectDetailURL;
    }
    return this.http.get<ProjectResult>(url.replace('--projectID--', projectID),
      {headers: HTTP_HEADERS})
      .pipe(
        tap(res => res),
        map((res) => {
          // Check if the response indicates an error
          if (!res.success || !res.result) {
            // Create an error object that matches the expected format
            const error = new Error(res.message || 'Project not found');
            (error as any).status = 404;
            (error as any).error = {
              errorCode: res.errorCode || 100100,
              message: res.message || 'Project not found'
            };
            throw error;
          }
          return res.result;
        }),
        catchError(error => {
          // Re-throw the error to be handled by the component
          return throwError(() => error);
        })
      );
  }

  public setProject(project: Project) {
    this.project = project;
  }


  public getThemeSettings() {
    return this.http.get<any>(themeSettingsURL)
      .pipe(
        tap(res => res)
      );
  }

  public setLogo(logo: string) {
    if (logo) {
      this.logo = logo;
    } else {
      this.logo = environment.defaultAppLogo;
    }
  }

  public getApplicationName() {
    // Prefer the loaded project's name, fallback to default title
    return this.project?.name || environment.defaultAppTitle;
  }

  public setLoading(loadingState: boolean) {
    this.loading = loadingState;
    this.globalLoadingSignal.set(loadingState);
  }

  public setGdprLink(gdprLink: string) {
    this.gdprLink = gdprLink;
  }

  public getContracts() {
    return this.http.get<any>(getContractsJSONUrl).pipe(
      tap(res => res)
    );
  }


  public isProjectActive() {
    if (!this.project.hasOwnProperty('active') || !this.project.active) {
      this.toastrService.error('The requested project is not active anymore');
      return false;
    }
    return true;
  }

  public setProductClaim(claim: string | null) {
    if (claim) {
      this.projectClaim = claim;
    }
  }

  public setProductLine1(productLine1: string | null) {
    if (productLine1) {
      this.projectLine1 = productLine1;
    }
  }

  public setProductLine2(productLine2: string | null) {
    if (productLine2) {
      this.projectLine2 = productLine2;
    }
  }
}
